import { Store } from "./state"
import { SearchQuery, SearchOptions } from "./api/types"
import { deepMerge, measure } from "@nosto/search-js/utils"
import { FullConfig } from "./config"
import { getFullUrl, getSearchUrl } from "./urlEncoding/url"
import { setErrorSession } from "./helpers/errorHandling"
import { restoreNativeContent, restoreNativeForm } from "./helpers/searchFallback"
import { newSearch as baseNewSearch, updateSearch as baseUpdateSearch } from "@nosto/search-js/preact/legacy"

export type FallbackConfig = Pick<FullConfig, "isCategoryPage" | "categoryFallback" | "serpFallback" | "fallback">

type ActionContext = {
    store: Store
    config: FullConfig
}

function createSearchContext(context: ActionContext) {
    return {
        config: {
            ...context.config,
            pageType: context.store.getState().pageType!,
            minQueryLength: context.config.autocompleteMinLength
        },
        store: context.store
    }
}

/**
 * Fallback predicate for Search API errors
 *
 *  Request aborted           -> no fallback
 *  status: 200               -> no fallback
 *  status: 400 (Bad request) -> no fallback
 *
 *  status: 408 (Timeout)     -> fallback
 *  Network Error             -> fallback
 *  status 502 (Bad Gateway)  -> fallback
 *  status 403 (Forbidden)    -> fallback
 *  search for this merchant is not ready yet, status 403 -> fallback
 *  some services are unavailable, response may be incomplete, status 500 -> fallback
 *  timeout exceeded          -> fallback
 */
export function shouldFallback(error: unknown) {
    if (!error) {
        return false
    } else if (error.toString().includes("Request aborted")) {
        return false
    } else if (typeof error === "object" && "status" in error && typeof error.status === "number") {
        return ![200, 400].includes(error.status)
    }
    return true
}

export async function newSearch(context: ActionContext, query: SearchQuery, options?: SearchOptions): Promise<void> {
    return baseNewSearch(createSearchContext(context), query, options)
}

export async function updateSearch(context: ActionContext, query: SearchQuery, options?: SearchOptions): Promise<void> {
    const fullOptions = deepMerge({ customParams: context.store.getState().customParams }, options)
    return baseUpdateSearch(createSearchContext(context), query, fullOptions)
}

export function simulateUpdateSearch(
    context: ActionContext,
    query: SearchQuery,
    options?: SearchOptions
): {
    url: URL
} {
    const fullQuery = deepMerge(context.store.getState().query, { products: { from: 0 } }, query)
    const fullOptions = deepMerge({ customParams: context.store.getState().customParams }, options)

    return {
        url: new URL(getFullUrl(getSearchUrl(context, fullQuery, fullOptions)))
    }
}

export function handleFallback(config: FallbackConfig, fullQuery: SearchQuery) {
    const end = measure("handleFallback")
    const { isCategoryPage, categoryFallback, serpFallback } = config

    if (isCategoryPage() && categoryFallback) {
        categoryFallback(fullQuery)
        return
    } else if (serpFallback) {
        serpFallback(fullQuery)
        return
    }

    const isErrorSet = setErrorSession()
    if (isErrorSet) {
        if (config.fallback === "legacy") {
            location.reload()
        } else {
            restoreNativeContent()
            restoreNativeForm()
        }
    }
    end()
}
